const KeenSlider = window.KeenSlider

const { setInitialised, useA11y, useArrows } = window.BAO.Carousel.plugins
const { setPreInitSlideA11yState } = window.BAO.Carousel

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',')

const ORIGINAL_TABINDEX_ATTRIBUTE = 'data-product-carousel-original-tabindex'

const setFocusableState = (slide, isHidden) => {
  const focusableElements = slide.querySelectorAll(FOCUSABLE_SELECTOR)

  focusableElements.forEach((element) => {
    if (isHidden) {
      if (!element.hasAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)) {
        const originalTabindex = element.getAttribute('tabindex')

        element.setAttribute(
          ORIGINAL_TABINDEX_ATTRIBUTE,
          originalTabindex ?? '',
        )
      }

      element.setAttribute('tabindex', '-1')
      return
    }

    if (!element.hasAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)) {
      return
    }

    const originalTabindex = element.getAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)

    if (originalTabindex === '') {
      element.removeAttribute('tabindex')
    } else {
      element.setAttribute('tabindex', originalTabindex)
    }

    element.removeAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)
  })
}

const getVisibleSlideIndexes = (container, slides, minimumVisiblePortion) => {
  const containerRect = container.getBoundingClientRect()

  return slides.reduce((visibleIndexes, slide, index) => {
    const slideRect = slide.getBoundingClientRect()
    const overlapWidth =
      Math.min(slideRect.right, containerRect.right) -
      Math.max(slideRect.left, containerRect.left)
    const overlapHeight =
      Math.min(slideRect.bottom, containerRect.bottom) -
      Math.max(slideRect.top, containerRect.top)

    if (overlapWidth <= 0 || overlapHeight <= 0) {
      return visibleIndexes
    }

    const overlapArea = overlapWidth * overlapHeight
    const slideArea = slideRect.width * slideRect.height

    if (slideArea <= 0) {
      return visibleIndexes
    }

    const visiblePortion = overlapArea / slideArea

    if (visiblePortion >= minimumVisiblePortion) {
      visibleIndexes.push(index)
    }

    return visibleIndexes
  }, [])
}

const setStrictPreInitSlideA11yState = (
  container,
  slideSelector,
  minimumVisiblePortion,
) => {
  if (!container || !slideSelector) {
    return
  }

  const slides = Array.from(container.querySelectorAll(slideSelector))

  if (!slides.length) {
    return
  }

  const visibleIndexes = getVisibleSlideIndexes(
    container,
    slides,
    minimumVisiblePortion,
  )
  const fallbackVisibleIndex = visibleIndexes.length > 0 ? null : 0

  slides.forEach((slide, index) => {
    const isVisible = visibleIndexes.length
      ? visibleIndexes.includes(index)
      : index === fallbackVisibleIndex
    const isHidden = !isVisible

    slide.setAttribute('aria-hidden', String(isHidden))
    setFocusableState(slide, isHidden)
  })
}

const setStrictRuntimeSlideA11yState = (carousel, minimumVisiblePortion) => {
  if (!carousel?.slides?.length) {
    return
  }

  const detailSlides = carousel.track?.details?.slides || []
  const relIndex = carousel.track?.details?.rel

  let visibleIndexes = detailSlides.reduce((indexes, slideDetail, index) => {
    if ((slideDetail?.portion || 0) >= minimumVisiblePortion) {
      indexes.push(index)
    }

    return indexes
  }, [])

  if (!visibleIndexes.length) {
    visibleIndexes = getVisibleSlideIndexes(
      carousel.container,
      carousel.slides,
      minimumVisiblePortion,
    )
  }

  const fallbackVisibleIndex =
    visibleIndexes.length > 0
      ? null
      : Number.isInteger(relIndex)
        ? relIndex
        : 0

  carousel.slides.forEach((slide, index) => {
    const isVisible = visibleIndexes.length
      ? visibleIndexes.includes(index)
      : index === fallbackVisibleIndex
    const isHidden = !isVisible

    slide.setAttribute('aria-hidden', String(isHidden))
    setFocusableState(slide, isHidden)
  })
}

const useStrictSlideVisibilityA11y = (minimumVisiblePortion) => {
  return (carousel) => {
    const updateSlideState = () => {
      setStrictRuntimeSlideA11yState(carousel, minimumVisiblePortion)
    }

    const handleTabKeydown = (event) => {
      if (event.key !== 'Tab') {
        return
      }

      window.requestAnimationFrame(updateSlideState)
    }

    const handleFocusIn = () => {
      updateSlideState()
    }

    const handleResize = () => {
      updateSlideState()
    }

    const addFallbackListeners = () => {
      carousel.container.addEventListener('keydown', handleTabKeydown)
      carousel.container.addEventListener('focusin', handleFocusIn)
      window.addEventListener('resize', handleResize)
    }

    const removeFallbackListeners = () => {
      carousel.container.removeEventListener('keydown', handleTabKeydown)
      carousel.container.removeEventListener('focusin', handleFocusIn)
      window.removeEventListener('resize', handleResize)
    }

    carousel.on('created', () => {
      addFallbackListeners()
      updateSlideState()
    })
    carousel.on('detailsChanged', updateSlideState)
    carousel.on('slideChanged', updateSlideState)
    carousel.on('animationEnded', updateSlideState)
    carousel.on('updated', updateSlideState)
    carousel.on('destroyed', removeFallbackListeners)
  }
}

class ProductCarousel extends window.BAO.CustomElement() {
  static MINIMUM_VISIBLE_PORTION = 0.6

  connectedCallback () {
    super.connectedCallback()

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    if (!this.els.slide?.exists) return

    // Keep the standard pre-init pass, then enforce stricter Product Carousel
    // visibility so partially visible cards are not tabbable.
    setPreInitSlideA11yState(this.els.slides.element, this.els.slide.selector)
    setStrictPreInitSlideA11yState(
      this.els.slides.element,
      this.els.slide.selector,
      ProductCarousel.MINIMUM_VISIBLE_PORTION,
    )

    const carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: {
          perView: 2,
          spacing: 10,
        },
        breakpoints: {
          '(min-width: 901px)': {
            slides: {
              perView: 3,
              spacing: 10,
            },
          },
          '(min-width: 1201px)': {
            slides: {
              perView: 4.1,
              spacing: 20,
            },
          },
        },
      },
      [
        setInitialised(),
        useA11y({
          label: 'Product carousel',
          instructions: null,
          announceSlides: true,
          announcementPrefix: 'Product carousel',
          includeTabindex: false,
        }),
        useStrictSlideVisibilityA11y(ProductCarousel.MINIMUM_VISIBLE_PORTION),
        useArrows({
          next: this.els?.next?.element,
          previous: this.els?.previous?.element,
        }),
      ],
    )

    this.setupArrowFocusManagement(carousel)

    return carousel
  }

  setupArrowFocusManagement (carousel) {
    let pendingFocusMove = false
    let lastFocusedSlideIndex = null
    let pendingTargetSlideIndex = null
    let pendingTabFocusTarget = null
    let pendingTabIntent = false
    let isKeyboardArrowActivation = false
    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const resolveTargetFocusable = () => {
      if (!Number.isInteger(pendingTargetSlideIndex)) {
        pendingTabFocusTarget = null
        return
      }

      const targetSlide = carousel.slides[pendingTargetSlideIndex]
      const firstFocusable = targetSlide?.querySelector(focusableSelector)

      pendingTabFocusTarget = firstFocusable || null

      if (pendingTabIntent && pendingTabFocusTarget) {
        pendingTabFocusTarget.focus({ preventScroll: true })
        pendingTabFocusTarget = null
        pendingTabIntent = false
      }
    }

    const onCarouselFocusIn = (event) => {
      const focusedSlide = event.target.closest(this.els.slide.selector)

      if (!focusedSlide) {
        return
      }

      lastFocusedSlideIndex = carousel.slides.indexOf(focusedSlide)
    }

    const resolvePendingTarget = () => {
      if (!pendingFocusMove) {
        return
      }

      pendingFocusMove = false
      resolveTargetFocusable()
    }

    carousel.on('animationEnded', resolvePendingTarget)

    this.els.slides.element.addEventListener('focusin', onCarouselFocusIn)

    const onArrowActivate = (direction, event) => {
      const wasKeyboardActivation =
        isKeyboardArrowActivation || event?.detail === 0

      isKeyboardArrowActivation = false

      if (!wasKeyboardActivation) {
        pendingFocusMove = false
        pendingTargetSlideIndex = null
        pendingTabFocusTarget = null
        pendingTabIntent = false
        return
      }

      const fallbackIndex = Number.isInteger(carousel.track?.details?.rel)
        ? carousel.track.details.rel
        : 0
      const baseSlideIndex = Number.isInteger(lastFocusedSlideIndex)
        ? lastFocusedSlideIndex
        : fallbackIndex
      const maxSlideIndex = carousel.slides.length - 1

      pendingTargetSlideIndex =
        direction === 'previous'
          ? Math.max(baseSlideIndex - 1, 0)
          : Math.min(baseSlideIndex + 1, maxSlideIndex)

      pendingFocusMove = true
    }

    const onArrowKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        isKeyboardArrowActivation = true
        return
      }

      if (event.key !== 'Tab' || event.shiftKey) {
        return
      }

      if (!pendingTabFocusTarget && !pendingFocusMove) {
        return
      }

      event.preventDefault()

      if (pendingTabFocusTarget) {
        pendingTabFocusTarget.focus({ preventScroll: true })
        pendingTabFocusTarget = null
        pendingTabIntent = false
        return
      }

      pendingTabIntent = true
    }

    this.els?.next?.element?.addEventListener(
      'click',
      (event) => {
        onArrowActivate('next', event)
      },
      true,
    )
    this.els?.previous?.element?.addEventListener(
      'click',
      (event) => {
        onArrowActivate('previous', event)
      },
      true,
    )

    this.els?.next?.element?.addEventListener('keydown', onArrowKeydown)
    this.els?.previous?.element?.addEventListener('keydown', onArrowKeydown)
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel)
}
